import React, { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { Menu, X, LogOut, LayoutDashboard, Phone } from "lucide-react";
import { Logo } from "@/components/Logo";
import { Button } from "@/components/ui/button";
import { useAuth } from "@/context/AuthContext";

const NAV = [
  { name: "Diagnostic IA", href: "#diagnostic-ia", testId: "header-diagnostic-link" },
  { name: "Assistant BTP", href: "#assistant-btp", testId: "header-chat-link" },
  { name: "Estimation", href: "#estimation", testId: "header-estimation-link" },
  { name: "Tarifs", href: "#tarifs", testId: "header-pricing-link" },
  { name: "Artisans", href: "#artisans", testId: "header-artisans-link" },
];

export const Header = ({ onLogin }) => {
  const { user, logout } = useAuth();
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 20);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <header
      className={`fixed top-0 left-0 right-0 z-50 transition-all duration-300 ${
        scrolled ? "bg-[#0A0F1A]/90 backdrop-blur-xl border-b border-white/10" : "bg-transparent"
      }`}
      data-testid="main-header"
    >
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 flex items-center justify-between h-16 lg:h-20">
        <Link to="/" className="shrink-0">
          <Logo size={28} />
        </Link>

        <nav className="hidden lg:flex items-center gap-7">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              data-testid={n.testId}
              className="text-sm font-medium text-white/70 hover:text-white transition-colors"
            >
              {n.name}
            </a>
          ))}
        </nav>

        <div className="hidden lg:flex items-center gap-3">
          <a
            href="tel:+33782151672"
            data-testid="header-call-btn"
            className="flex items-center gap-2 text-sm font-medium text-white/80 hover:text-[#00C3A7] transition-colors"
          >
            <Phone className="w-4 h-4" /> +33 7 82 15 16 72
          </a>
          {user ? (
            <>
              <Button
                variant="ghost"
                className="text-white hover:bg-white/10"
                data-testid="header-dashboard-btn"
                onClick={() => navigate("/dashboard")}
              >
                <LayoutDashboard className="w-4 h-4 mr-2" /> Mon Espace
              </Button>
              <Button
                variant="ghost"
                className="text-white/70 hover:bg-white/10"
                data-testid="header-logout-btn"
                onClick={logout}
              >
                <LogOut className="w-4 h-4" />
              </Button>
            </>
          ) : (
            <Button
              variant="ghost"
              className="text-white hover:bg-white/10"
              data-testid="header-login-btn"
              onClick={onLogin}
            >
              Connexion
            </Button>
          )}
          <Button
            className="bg-[#0D6EFD] hover:bg-[#0b5ed7] text-white rounded-full px-5 shadow-lg shadow-[#0D6EFD]/30"
            data-testid="header-cta-quote-btn"
            onClick={() => {
              document.getElementById("tarifs")?.scrollIntoView({ behavior: "smooth" });
            }}
          >
            Faire analyser un devis
          </Button>
        </div>

        <button
          className="lg:hidden text-white p-2"
          onClick={() => setOpen(!open)}
          data-testid="header-mobile-toggle"
          aria-label="Menu"
        >
          {open ? <X /> : <Menu />}
        </button>
      </div>

      {open && (
        <div className="lg:hidden bg-[#0A0F1A]/98 backdrop-blur-xl border-t border-white/10 px-4 py-4 space-y-2">
          {NAV.map((n) => (
            <a
              key={n.href}
              href={n.href}
              onClick={() => setOpen(false)}
              className="block py-2 text-white/80 font-medium"
            >
              {n.name}
            </a>
          ))}
          <div className="pt-2 flex flex-col gap-2">
            <a href="tel:+33782151672" className="flex items-center gap-2 py-2 text-[#00C3A7] font-medium">
              <Phone className="w-4 h-4" /> +33 7 82 15 16 72
            </a>
            {user ? (
              <>
                <Button onClick={() => { setOpen(false); navigate("/dashboard"); }} className="bg-[#0D6EFD] text-white w-full">
                  Mon Espace
                </Button>
                <Button variant="outline" onClick={() => { setOpen(false); logout(); }} className="w-full">
                  Déconnexion
                </Button>
              </>
            ) : (
              <Button onClick={() => { setOpen(false); onLogin(); }} className="bg-[#0D6EFD] text-white w-full">
                Connexion / Mon Espace
              </Button>
            )}
          </div>
        </div>
      )}
    </header>
  );
};
